import createClient from "../../helpers/api-client";

import { UPDATE_COMPANIES, GET_CONTRACTOR_IDS } from "./queued-companies-model";

import {
    isEmailValid,
    isPhoneValid,
    isPostalWithCityValid,
    isSocialSecurityNumberValid
} from '../../helpers/error-validation';
import { FETCH_FILTERS } from './filters-model';

const storeSuffix = "RequestForm";
const sessionStorageExternalId = "externalId";

// actions
export const FETCH_FORM_BY_ID = `fetchRequestFormById${storeSuffix}`;
export const UPSERT_ANSWER = `upsertFormAnswer${storeSuffix}`;
export const SEND_FORM_RESULT = `sendFormResult${storeSuffix}`;
export const VALIDATE_FORM = `validateForm${storeSuffix}`;
export const VALIDATE_SINGLE_QUESTION = `validateSingleQuestion${storeSuffix}`;
export const RESET_ERROR = `resetError${storeSuffix}`;

// mutations
export const BEGIN_FETCH_FORM = `loading${storeSuffix}`;
export const FETCH_FORM_DONE = `fetchDone${storeSuffix}`;
export const SET_FORM_ERROR = `fetchError${storeSuffix}`;
export const RESET_FORM_DATA = `resetFormData${storeSuffix}`;
export const SEND_FORM_DONE = `sendDone${storeSuffix}`;
export const ADD_FILES = `addFile${storeSuffix}`;
export const ADD_DYNAMIC_FORM_ANSWER = `addDynamicFormAnswer${storeSuffix}`;
export const REMOVE_DYNAMIC_FORM_ANSWER = `removeDynamicFormAnswer${storeSuffix}`;
export const ADD_STATIC_FORM_ANSWER = `addStaticFormAnswer${storeSuffix}`;
export const REMOVE_STATIC_FORM_ANSWER = `removeStaticFormAnswer${storeSuffix}`;
export const ADD_FORM_ANSWER_POINTER = `addFormAnswerPointer${storeSuffix}`;
export const REMOVE_FORM_ANSWER_POINTER = `removeFormAnswerPointer${storeSuffix}`;
export const UPDATE_CONDITIONS = `updateFormConditions${storeSuffix}`;
export const UPDATE_SIZE_CONDITION = `updateProjectSizeConditions${storeSuffix}`;
export const VALIDATE_QUESTION = `validateQuestion${storeSuffix}`;
export const FORM_IS_VALID = `formIsValid${storeSuffix}`;

// getters
export const GET_FORM = `getForm${storeSuffix}`;
export const GET_ERROR = `getFormError${storeSuffix}`;
export const GET_CREATED_REQUEST = `getCreatedRequest${storeSuffix}`;
export const IS_LOADING = `getFormIsLoading${storeSuffix}`;
export const IS_FORM_VALID = `isFormValid${storeSuffix}`;

// helpers
const conditionsFullfilled = (conditionStr, answerSet, largeJob = false) => {
    const conditionArr = conditionStr.split(',');
    const conditionResults = conditionArr.map(condition => {
        let pointers;
        // Check for else operator (||)
        if (condition.indexOf("||") > -1) {
            // split the condition and check if any is met
            pointers = condition.split("||");

            for (let i = 0; i < pointers.length; i++) {
                if (pointers[i].indexOf("!") > -1) {
                    let trimmedPointer = pointers[i].trim().split("!")[1];
                    if (!matchPoint(trimmedPointer, answerSet)) {
                        return true;
                    }
                } else if (matchPoint(pointers[i], answerSet)) {
                    return true;
                }
            }
            return false;
        }
        // if there are more choices selected than the given value (ex. condition: "1+3,<4;25")
        if (condition.indexOf("+") > -1) {
            pointers = condition.split("+");
            const questionId = pointers[0];
            const questionValue = pointers[1];

            return answerSet[questionId] &&
                answerSet[questionId].length > questionValue
                ? true
                : false;
        }

        if (condition.indexOf(">") > -1) {
            pointers = condition.split(">");
            return smallerOrEqualThan(pointers[1], answerSet);
        }
        if (condition.indexOf("<") > -1) {
            pointers = condition.split("<");
            return !smallerOrEqualThan(pointers[1], answerSet);
        }

        // if all conditions except one are true
        if (condition.indexOf("!") > -1) {
            let trimmedPointer = condition.split("!");
            if (!matchPoint(trimmedPointer[1], answerSet)) {
                return true;
            }
        }
        if(condition.indexOf('^') > -1) {
             return largeJob;
        }
        // if no else found, we use and (&&)
        return matchPoint(condition, answerSet);
    });
    // return if all conditions are met or not
    return conditionResults.indexOf(false) == -1;
};

const matchPoint = (condition, dataSet) => {
    let conditions = splitCondition(condition);

    return dataSet[conditions[0]]
        ? dataSet[conditions[0]] == conditions[1]
        : false;
};

const notMatchPoint = (condition, dataSet) => {
    let conditions = splitCondition(condition);

    return dataSet[conditions[0]]
        ? dataSet[conditions[0]] != conditions[1]
        : false;
};

const smallerOrEqualThan = (condition, dataSet) => {
    let conditions = splitCondition(condition);
    let input = 0;
    if(dataSet[conditions[0]]){
        input = parseInt(dataSet[conditions[0]]) || 0;
    }
    return input <= conditions[1];
};

const splitCondition = condition => {
    return condition.trim().split(";");
};

const extractPointer = question => {
    const answers = question.answers;
    if (typeof answers !== 'boolean' && question.dataKey || question.text || question.file ) return;

    let answerIds;
    if (question.multi) {
        answerIds = answers.map(item => {
            return item.id;
        });
    } else if (typeof answers === 'boolean') {
        answerIds = answers ? 1 : 0
    }else if (question.number || question.single || question.radio) {
        answerIds = parseFloat(answers);
    }  else {
        answerIds = answers.id;
    }

    return answerIds;
};

export const modelState = () => ({
    requestForm: {
        isLoading: false,
        error: "",
        createdRequest: null,
        form: {
            data: {},
            answersPointers: {},
            answersData: {
                title: null,
                Fields: {},
                attachment: { files: [], isValid: true }
            },
            projectSize: "small",
            formIsValid: false
        }
    }
});

export const actions = {
    [FETCH_FORM_BY_ID]: ({ state, commit, dispatch }, id) => {
        return new Promise(async (resolve, reject) => {
            commit(RESET_FORM_DATA);
            commit(BEGIN_FETCH_FORM);

            await dispatch(FETCH_FILTERS);

            if (!id) {
                const fakeRes = {
                    data: {}
                };
                commit(FETCH_FORM_DONE, fakeRes);
                return resolve(fakeRes);
            }

            const apiClient = createClient(state);

            try {
                const res = await apiClient.get(`/api/request-form/${id}`);

                commit(FETCH_FORM_DONE, res);
                commit(UPDATE_SIZE_CONDITION);
                commit(UPDATE_CONDITIONS);

                resolve(res);
            } catch (error) {
                commit(SET_FORM_ERROR, "not-fetched");
                resolve(error);
            }
        });
    },
    [SEND_FORM_RESULT]: ({ state, commit, rootState, rootGetters }) => {
        return new Promise(async (resolve, reject) => {
            const apiClient = createClient(state);

            commit(BEGIN_FETCH_FORM);
            try {
                let formData = new FormData();

                if (
                    typeof state.requestForm.form.answersData.attachment
                        .files !== "undefined"
                ) {
                    for (
                        var i = 0;
                        i <
                        state.requestForm.form.answersData.attachment.files
                            .length;
                        i++
                    ) {
                        let file =
                            state.requestForm.form.answersData.attachment.files[
                                i
                            ];

                        formData.append("files[" + i + "]", file);
                    }
                }


                formData.append(
                    "Fields",
                    JSON.stringify(state.requestForm.form.answersData.Fields)
                );

                if(!state.requestForm.form.answersData['SecureJob'])
                    formData.append('SecureJob', false);

                Object.keys(state.requestForm.form.answersData).forEach(key => {
                    if (key == "Fields" || key == "Files" || key == "title")
                        return;

                    formData.append(
                        key,
                        state.requestForm.form.answersData[key]
                    );
                });

                //If companies has been added to queued-companies
                const contractorIds = rootGetters[GET_CONTRACTOR_IDS];
                if (contractorIds && contractorIds.length > 0) {
                    formData.append(
                        "CompanyIds",
                        JSON.stringify(contractorIds)
                    );
                }

                let title = state.requestForm.form.answersData.title;

                if (!title || title.length < 1) {
                    let regionName = rootGetters.getRegionsFiltersGetterFilters.find(
                        region => {
                            region.Subcategories.find(sub => {
                                return (
                                    sub.Id ===
                                    state.requestForm.form.answersData.RegionId
                                );
                            });
                        }
                    );

                    title = `${state.requestForm.form.data.subCategory} i ${regionName}`;
                }

                formData.append("Title", title);

                formData.append(
                    "CategoryId",
                    state.requestForm.form.data.subCategoryId
                );
                formData.append("Budget", state.requestForm.form.projectSize);

                if (typeof window !== 'undefined') {
                    const extId = sessionStorage.getItem(sessionStorageExternalId);
                    if (extId) {
                        formData.append("ExternalId", extId);
                    }
                }

                const res = await apiClient.post(
                    `/api/request-form`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                commit(SEND_FORM_DONE, res.data);
                // Reset companies queue
                commit(UPDATE_COMPANIES, [], { root: true });

                resolve(true);
            } catch (error) {
                commit(SET_FORM_ERROR, "not-sent");
                reject(error);
            }
        });
    },
    [UPSERT_ANSWER]: ({ state, commit }, data) => {
        const dataKey = data.dataKey;
        const questionId = data.questionId;
        const question = data.label;
        const answer = data.answers;
        let answerText;
        if (data.text && answer != null && answer !== "") {
            answerText = answer;
        } else if (data.single && answer != null && answer !== false) {
            answerText = answer;
        } else if (data.radio && answer != null) {
            answerText = answer === "1" ? "Ja" : "Nej";
        } else if (data.number && answer != null && answer > 0) {
            answerText = answer.toString().replace(/[.]/, ",") + data.unit;
        } else if (data.multi && answer != null && answer.length > 0) {
            answerText = answer
                .map(item => {
                    return item.text;
                })
                .join(", ");
        } else {
            answerText = answer.text;
        }

        if (dataKey) {
            if (answerText == null) {
                commit(REMOVE_STATIC_FORM_ANSWER, {
                    dataKey
                });
            } else {
                commit(ADD_STATIC_FORM_ANSWER, {
                    dataKey,
                    answer: answerText
                });
            }
        } else {
            if (answerText == null) {
                commit(REMOVE_DYNAMIC_FORM_ANSWER, {
                    question
                });
            } else {
                commit(ADD_DYNAMIC_FORM_ANSWER, {
                    questionId,
                    question,
                    answer: answerText
                });
            }
        }

        const answerIds = extractPointer(data);

        if (answerIds == null) {
            commit(REMOVE_FORM_ANSWER_POINTER, {
                questionId
            });
        } else {
            commit(ADD_FORM_ANSWER_POINTER, {
                questionId,
                answerIds
            });
        }
        commit(UPDATE_SIZE_CONDITION);
        commit(UPDATE_CONDITIONS);
 
    },
    [VALIDATE_SINGLE_QUESTION]: ({ state, commit }, data) => {
        return new Promise(async (resolve, reject) => {
            const step = data.step;
            const questions = state.requestForm.form.data.steps[
                data.step
            ].questions.filter(q => q.id == data.questionId);

            questions.forEach(question => {
                let isQuestionValid = true;

                let answer;
                if (question.dataKey) {
                    answer =
                        state.requestForm.form.answersData[question.dataKey];
                } else {
                    answer =
                        state.requestForm.form.answersData.Fields[
                            question.label
                        ];
                }

                if (question.required && !question.disabled) {
                    if (answer == null) {
                        isQuestionValid = false;
                    }
                }
             
                if (question.dataKey && !question.disabled) {
                    if (
                        answer != null &&
                        answer !== "" &&
                        ((question.dataKey === "Email" &&
                            !isEmailValid(answer)) ||
                            (question.dataKey === "Phone" &&
                                !isPhoneValid(answer)) ||
                            (question.dataKey === 'PostalWithCity' &&
                                !isPostalWithCityValid(answer)) || 
                            (question.dataKey === "SocialSecurityNumber" &&
                                !isSocialSecurityNumberValid(answer)) 
                                
                                )
                    ) {
                        isQuestionValid = false;
                    }
                }

                commit(VALIDATE_QUESTION, {
                    step,
                    question,
                    questionValid: isQuestionValid
                });
            });

            resolve();
        });
    },
    [VALIDATE_FORM]: ({ state, commit }) => {
        return new Promise(async (resolve, reject) => {
            let formValid = true;
            
            state.requestForm.form.data.steps.forEach((step, stepIndex) => {
                step.questions.forEach(question => {
                    let isQuestionValid = true;

                    let answer;
                    if (question.dataKey) {
                        answer =
                            state.requestForm.form.answersData[
                                question.dataKey
                            ];
                    } else {
                        answer =
                            state.requestForm.form.answersData.Fields[
                                question.label
                            ];
                    }

                    if (question.required && !question.disabled) {
                        if (answer == null) {
                            isQuestionValid = false;
                            formValid = false;
                        }
                    }

                    if (question.dataKey && !question.disabled) {
                        if (answer != null && answer !== "") {
                            if (
                                question.dataKey === "Email" &&
                                !isEmailValid(answer)
                            ) {
                                isQuestionValid = false;
                                formValid = false;
                            }
                            if (
                                question.dataKey === "Phone" &&
                                !isPhoneValid(answer)
                            ) {
                                isQuestionValid = false;
                                formValid = false;
                            }
                            if (
                                question.dataKey === "PostalWithCity" &&
                                !isPostalWithCityValid(answer)
                            ) {
                                isQuestionValid = false;
                                formValid = false;
                            }

                            if(question.dataKey === 'SocialSecurityNumber' && !isSocialSecurityNumberValid(answer)){
                                isQuestionValid = false;
                                formValid = false;
                            }
                            
                        }

                        // TODO: Validate Filess
                        if (
                            question.dataKey === "files" &&
                            !state.requestForm.form.answersData.attachment
                                .isValid
                        ) {
                            isQuestionValid = false;
                            formValid = false;
                        }
                    }

                    commit(VALIDATE_QUESTION, {
                        step: stepIndex,
                        question,
                        questionValid: isQuestionValid
                    });
                });
            });

            commit(FORM_IS_VALID, { isValid: formValid });
            resolve();
        });
    },
    [RESET_ERROR]: ({ state, commit }) => {
        commit(SET_FORM_ERROR, "");
    }
};

export const mutations = {
    [BEGIN_FETCH_FORM]: state => {
        state.requestForm.isLoading = true;
        state.requestForm.error = "";
    },
    [FETCH_FORM_DONE]: (state, payload) => {
        const { data } = payload;
        state.requestForm.isLoading = false;
        state.requestForm.error = "";
        state.requestForm.form.data = data;
    },
    [SEND_FORM_DONE]: (state, payload) => {
        state.requestForm.isLoading = false;
        state.requestForm.error = "";
        state.requestForm.createdRequest = payload;
    },
    [SET_FORM_ERROR]: (state, error) => {
        state.requestForm.isLoading = false;
        state.requestForm.error = error;
    },
    [ADD_FILES]: (state, { files, validSize, Id, step }) => {
        let question = state.requestForm.form.data.steps[step].questions.find(
            function(element) {
                return element.id === Id;
            }
        );
        if(!validSize) {
            question.invalid = true;
            state.requestForm.form.answersData.attachment.isValid = false;
        } else {
            question.invalid = false;
            state.requestForm.form.answersData.attachment.isValid = true;
        }
        state.requestForm.form.answersData.attachment.files = files;
    },
    [ADD_STATIC_FORM_ANSWER]: (state, { dataKey, answer }) => {
        state.requestForm.form.answersData[dataKey] = answer;
    },
    [REMOVE_STATIC_FORM_ANSWER]: (state, { dataKey }) => {
        delete state.requestForm.form.answersData[dataKey];
    },
    [ADD_DYNAMIC_FORM_ANSWER]: (state, { questionId, question, answer }) => {
        state.requestForm.form.answersData.Fields[question] = answer;
    },
    [REMOVE_DYNAMIC_FORM_ANSWER]: (state, { question }) => {
        delete state.requestForm.form.answersData.Fields[question];
    },
    [ADD_FORM_ANSWER_POINTER]: (state, { questionId, answerIds }) => {
        state.requestForm.form.answersPointers[questionId] = answerIds; //state.requestForm.form.answersPointers.concat(pointer);
    },
    [REMOVE_FORM_ANSWER_POINTER]: (state, { questionId }) => {
        delete state.requestForm.form.answersPointers[questionId];
    },
    [UPDATE_CONDITIONS]: state => {
        if (!state.requestForm.form.data.steps) {
            return;
        }
        state.requestForm.form.data.steps.forEach(theStep => {
            if (!theStep.questions) return;
            theStep.questions.forEach(item => {
                if (!item.condition) return;
                if (
                    !conditionsFullfilled(
                        item.condition,
                        state.requestForm.form.answersPointers,
                        state.requestForm.form.projectSize === 'large'
                    )
                ) {
                    item.disabled = true;
                    // ta bort datan för item
                    delete state.requestForm.form.answersData.Fields[
                        item.label
                    ];
                    delete state.requestForm.form.answersPointers[item.id];
                    // Might need to remove static form answer as well (dataKey)?
                } else {
                    item.disabled = false;
                }
            });
        });
    },
    [UPDATE_SIZE_CONDITION]: state => {
        if (
            state.requestForm.form.data.large &&
            conditionsFullfilled(
                state.requestForm.form.data.large,
                state.requestForm.form.answersPointers
            )
        ) {
            state.requestForm.form.projectSize = "large";
            return;
        }

        if (
            state.requestForm.form.data.medium &&
            conditionsFullfilled(
                state.requestForm.form.data.medium,
                state.requestForm.form.answersPointers
            )
        ) {
            state.requestForm.form.projectSize = "medium";
            return;
        }

        state.requestForm.form.projectSize = "small";
    },
    [VALIDATE_QUESTION]: (state, { step, question, questionValid }) => {
        let id = state.requestForm.form.data.steps[step].questions.indexOf(
            question
        );

        if (questionValid) {
            state.requestForm.form.data.steps[step].questions[
                id
            ].invalid = false;
        } else {
            state.requestForm.form.data.steps[step].questions[
                id
            ].invalid = true;
        }
    },
    [FORM_IS_VALID]: (state, { isValid }) => {
        state.requestForm.form.formIsValid = isValid;
    },
    [RESET_FORM_DATA]: state => {
        state.requestForm = {
            isLoading: false,
            error: "",
            createdRequest: null,
            form: {
                data: {},
                answersPointers: {},
                answersData: {
                    Fields: {},
                    attachment: { files: [], isValid: true }
                },
                projectSize: "small",
                formIsValid: false
            }
        };
    }
};

export const getters = {
    [GET_FORM]: state => {
        return state.requestForm.form;
    },
    [IS_LOADING]: state => {
        return state.requestForm.isLoading;
    },
    [GET_CREATED_REQUEST]: state => {
        return state.requestForm.createdRequest;
    },
    [GET_ERROR]: state => {
        return state.requestForm.error;
    },
    [IS_FORM_VALID]: state => {
        return state.requestForm.form.formIsValid;
    }
};



// WEBPACK FOOTER //
// ./src/store/store-models/request-form-model.js